// src/hooks/useKatmanUrl.ts
//
// KATMAN ↔ ADRES ÇUBUĞU KÖPRÜSÜ — TEK UYGULAMA.
//
// Bu depoda tekrar eden kusur sınıfı "aynı kuralın ikinci kopyası"dır
// (rezerve adlar · katman kütüğü · rewrite işleyicileri). Anlatım yüzeyleri
// (`/info/{konu}`) ve koleksiyon detayı (`/u/{uid}/collections/{id}`) AYNI
// URL kuralına tabi; bu yüzden kural bir kez burada yazılır, iki sarmalayıcı
// (`useBilgiSayfasiUrl`, `useKoleksiyonDetayUrl`) yalnız HEDEFİ üretir.
//
// KURAL: katman → PUSH (mod olsaydı replace olurdu; bkz. discoveryUrl.ts
// "modlar replace, katmanlar push"). Sheet bir katmandır: donanım GERİ tuşu
// onu kapatmalı, kullanıcıyı alttaki sayfadan ATMAMALI.
//   - Açılış   : mevcut yolu sakla + hedefe pushState.
//   - Kapanış  : biz push'ladıysak `history.back()` (X ve GERİ tek kapıdan
//                çıkar, stack şişmez) — `restoreDiscoveryUrl` deseni.
//   - Soğuk yük: URL zaten hedefte → push YOK; kapanışta `back()` kullanıcıyı
//                SİTEDEN ÇIKARIRDI → saklı yol yok, köke replace.
//   - GERİ tuşu: popstate'te yol artık bu katmanın ailesinden değilse katman
//                KAPANIR (#837'nin "URL değişti, katman kapanmadı" kusuru).

import { useEffect, useRef } from "react";

export type KatmanUrlSecenekleri = {
  /** Katmanın canonical yolu. `null` → URL yönetimi devre dışı (no-op). */
  hedef: string | null;
  open: boolean;
  onClose: () => void;
  /** Verilen yol bu katmanın AİLESİNDEN mi? (kapanış/popstate kararı) */
  yoldaMi: (path: string) => boolean;
};

export default function useKatmanUrl({
  hedef,
  open,
  onClose,
  yoldaMi,
}: KatmanUrlSecenekleri): void {
  const pushluRef = useRef(false);
  const oncekiYolRef = useRef<string | null>(null);
  const oncekiAcikRef = useRef(false);
  // onClose/yoldaMi kimliği her render'da değişebiliyor (çağrı yerleri
  // satır-içi ok fonksiyonu veriyor). Ref'te tutulmazsa popstate dinleyicisi
  // her render'da kopar-tak olurdu.
  const kapatRef = useRef(onClose);
  kapatRef.current = onClose;
  const yoldaMiRef = useRef(yoldaMi);
  yoldaMiRef.current = yoldaMi;

  useEffect(() => {
    if (!hedef || typeof window === "undefined") return;
    const oncekiAcik = oncekiAcikRef.current;
    oncekiAcikRef.current = open;

    // AÇILDI (false→true). İlk mount'ta open=false ise hiçbir dal çalışmaz.
    if (open && !oncekiAcik) {
      const simdi = window.location.pathname + window.location.search;
      if (simdi === hedef) {
        // Soğuk yük / paylaşılan link: URL zaten hedefte → push YOK.
        pushluRef.current = false;
        oncekiYolRef.current = null;
        return;
      }
      oncekiYolRef.current = simdi;
      try {
        window.history.pushState(window.history.state, "", hedef);
        pushluRef.current = true;
      } catch {
        pushluRef.current = false;
      }
      return;
    }

    // KAPANDI (true→false).
    if (!open && oncekiAcik) {
      // Zaten GERİ tuşuyla çıkılmışsa (popstate dalı) yapacak bir şey yok.
      if (!yoldaMiRef.current(window.location.pathname)) {
        pushluRef.current = false;
        oncekiYolRef.current = null;
        return;
      }
      if (pushluRef.current) {
        pushluRef.current = false;
        oncekiYolRef.current = null;
        try { window.history.back(); } catch { /* no-op */ }
        return;
      }
      const geri = oncekiYolRef.current;
      oncekiYolRef.current = null;
      try {
        window.history.replaceState(window.history.state, "", geri || "/");
      } catch { /* no-op */ }
    }
  }, [open, hedef]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    const onPop = () => {
      if (yoldaMiRef.current(window.location.pathname)) return;
      // Girdi ZATEN pop'landı → kapanış dalında ikinci bir `back()` çağırma
      // (bir adım fazla geri gider, kullanıcıyı beklenmedik yere atardı).
      pushluRef.current = false;
      oncekiYolRef.current = null;
      try { kapatRef.current(); } catch { /* no-op */ }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);
}
